import {
  type ReactElement,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { AppLocale } from '@nest-react/lib';
import { Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useAuth, useWorkspace } from '~/app/providers';
import { type ThemeMode, useTheme } from '~/app/theme';
import { AppImage } from '~/components/ui/AppImage';
import { Icon } from '~/components/ui/Icon';
import { useBillingUpgrade } from '~/features/billing/BillingUpgradeProvider';
import { useWorkspaceBillingSummary } from '~/stores/workspace';
import { cn } from '~/utils';

interface AccountSettingsPopoverProps {
  open: boolean;
  onClose: () => void;
  triggerRef: RefObject<HTMLButtonElement | null>;
  onSwitchWorkspace?: () => void;
  suspended?: boolean;
  workspaceTriggerRef?: RefObject<HTMLButtonElement | null>;
}

const SETTINGS_ACTIONS = [
  { key: 'plansAndBilling', icon: 'card' },
  { key: 'accountProfile', icon: 'account' },
  { key: 'upgradePlan', icon: 'gift' },
  { key: 'developerApi', icon: 'code' },
] as const;

const LOCALE_OPTIONS: { value: AppLocale; label: string }[] = [
  { value: 'ru', label: 'Русский' },
  { value: 'en', label: 'English' },
  { value: 'zh-Hans', label: '简体中文' },
];

const THEME_OPTIONS: {
  value: ThemeMode;
  label: 'system' | 'light' | 'dark';
  icon: string;
}[] = [
  { value: 'system', label: 'system', icon: 'monitor' },
  { value: 'light', label: 'light', icon: 'sun' },
  { value: 'dark', label: 'dark', icon: 'moon' },
];

const optionClass =
  'flex min-h-10 w-full items-center gap-3 rounded-xl px-3 text-left text-sm transition focus-visible:outline-2 focus-visible:outline-primary';

export const AccountSettingsPopover = ({
  open,
  onClose,
  triggerRef,
  onSwitchWorkspace = () => undefined,
  suspended = false,
  workspaceTriggerRef,
}: AccountSettingsPopoverProps): ReactElement | null => {
  const { t } = useTranslation('account');
  const { t: tb } = useTranslation('billing');
  const { session, signOut } = useAuth();
  const { activeWorkspace, locale, setLocale, workspaceContracts } =
    useWorkspace();
  const { theme, setTheme } = useTheme();
  const { openUpgrade } = useBillingUpgrade();
  const { summary: billingSummary } = useWorkspaceBillingSummary(
    activeWorkspace?.id
  );
  const dialogRef = useRef<HTMLDivElement>(null);
  const [expandedPreference, setExpandedPreference] = useState<
    'theme' | 'language' | null
  >(null);
  const [localeSaving, setLocaleSaving] = useState(false);

  const close = useCallback(() => {
    onClose();
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  }, [onClose, triggerRef]);

  useEffect(() => {
    if (!open || suspended) return undefined;

    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target as Node;
      if (
        !dialogRef.current?.contains(target) &&
        !triggerRef.current?.contains(target)
      ) {
        close();
      }
    };
    const onFocusIn = (event: FocusEvent): void => {
      const target = event.target as Node;
      if (
        !dialogRef.current?.contains(target) &&
        !triggerRef.current?.contains(target)
      ) {
        close();
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [close, open, suspended, triggerRef]);

  if (!open || !session) return null;

  const initials = session.user.displayName.slice(0, 2).toUpperCase();
  const currentPlan = billingSummary?.subscription?.plan;
  const togglePreference = (preference: 'theme' | 'language'): void => {
    setExpandedPreference(current =>
      current === preference ? null : preference
    );
  };
  const selectLocale = async (next: AppLocale): Promise<void> => {
    if (next === locale || localeSaving) return;
    setLocaleSaving(true);
    try {
      await setLocale(next);
    } catch {
      // The provider restores the previous locale when persistence fails.
    } finally {
      setLocaleSaving(false);
    }
  };

  return (
    <div
      aria-label={t('title')}
      className="absolute right-0 top-11 z-50 w-[min(20rem,calc(100vw-2rem))] rounded-2xl border border-border bg-elevated shadow-2xl"
      id="account-settings-popover"
      ref={dialogRef}
      role="dialog"
    >
      <div className="border-b border-border-subtle p-4">
        <div className="flex items-center gap-3">
          <span className="grid size-10 shrink-0 place-items-center overflow-hidden rounded-full bg-gradient-to-br from-primary via-violet-600 to-fuchsia-700 text-xs font-bold text-white">
            {session.user.avatarUrl ? (
              <AppImage
                alt=""
                className="size-full object-cover"
                height={40}
                src={session.user.avatarUrl}
                width={40}
                wrapperClassName="size-full"
              />
            ) : (
              initials
            )}
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex min-w-0 items-center gap-2">
              <strong className="truncate text-sm text-foreground">
                {session.user.displayName}
              </strong>
              <span aria-live="polite" className="inline-flex shrink-0">
                {currentPlan ? (
                  <span
                    aria-label={`${tb('currentPlan')}: ${tb(`planNames.${currentPlan}`)}`}
                    className="inline-flex min-h-5 items-center rounded-full border border-primary/20 bg-primary/10 px-2 font-mono text-[10px] font-semibold leading-none tracking-wide text-primary"
                  >
                    {tb(`planNames.${currentPlan}`)}
                  </span>
                ) : null}
              </span>
            </span>
            <small className="block truncate text-muted">
              {session.user.email ?? 'Account'}
            </small>
          </span>
        </div>
      </div>

      <div className="grid p-1.5">
        {SETTINGS_ACTIONS.map(action => {
          if (
            action.key === 'developerApi' &&
            activeWorkspace?.role !== 'OWNER' &&
            activeWorkspace?.role !== 'ADMIN'
          ) {
            return null;
          }
          if (action.key === 'upgradePlan') {
            return (
              <button
                className={cn(
                  optionClass,
                  'text-muted hover:bg-hover hover:text-foreground'
                )}
                key={action.key}
                onClick={() => {
                  onClose();
                  openUpgrade();
                }}
                type="button"
              >
                <Icon className="size-[18px]" name={action.icon} />
                {t(action.key)}
              </button>
            );
          }
          const target =
            action.key === 'accountProfile'
              ? '/account/profile'
              : action.key === 'developerApi'
                ? '/workspaces/developer-api'
                : '/workspaces/billing';
          return (
            <Link
              className={cn(
                optionClass,
                'text-muted hover:bg-hover hover:text-foreground'
              )}
              key={action.key}
              onClick={close}
              to={target}
            >
              <Icon className="size-[18px]" name={action.icon} />
              {t(action.key)}
            </Link>
          );
        })}
      </div>

      <div className="relative border-y border-border-subtle p-1.5">
        <button
          aria-controls="theme-options"
          aria-expanded={expandedPreference === 'theme'}
          className={cn(
            optionClass,
            'text-muted hover:bg-hover hover:text-foreground'
          )}
          onClick={() => togglePreference('theme')}
          type="button"
        >
          <Icon className="size-[18px]" name="monitor" />
          <span className="flex-1">{t('theme')}</span>
          <span className="text-xs text-subtle">{t(theme)}</span>
          <Icon
            className={cn(
              'size-4 transition-transform',
              'motion-reduce:transition-none',
              expandedPreference === 'theme' && 'rotate-90'
            )}
            name="arrow"
          />
        </button>
        {expandedPreference === 'theme' && (
          <div
            aria-label={t('themeOptions')}
            className="absolute bottom-0 right-0 z-10 grid w-[calc(100%_-_0.75rem)] rounded-xl border border-border bg-elevated p-1.5 shadow-2xl sm:right-[calc(100%+0.5rem)] sm:w-48"
            id="theme-options"
            role="group"
          >
            {THEME_OPTIONS.map(option => (
              <button
                className={cn(
                  optionClass,
                  theme === option.value
                    ? 'bg-hover text-foreground'
                    : 'text-muted hover:bg-hover hover:text-foreground'
                )}
                key={option.value}
                onClick={() => setTheme(option.value)}
                type="button"
              >
                <Icon className="size-[18px]" name={option.icon} />
                <span className="flex-1">{t(option.label)}</span>
                {theme === option.value && (
                  <Icon className="size-4" name="check" />
                )}
              </button>
            ))}
          </div>
        )}

        <button
          aria-controls="language-options"
          aria-expanded={expandedPreference === 'language'}
          className={cn(
            optionClass,
            'text-muted hover:bg-hover hover:text-foreground'
          )}
          onClick={() => togglePreference('language')}
          type="button"
        >
          <Icon className="size-[18px]" name="globe" />
          <span className="flex-1">{t('language')}</span>
          <span className="text-xs text-subtle">
            {LOCALE_OPTIONS.find(option => option.value === locale)?.label}
          </span>
          <Icon
            className={cn(
              'size-4 transition-transform',
              'motion-reduce:transition-none',
              expandedPreference === 'language' && 'rotate-90'
            )}
            name="arrow"
          />
        </button>
        {expandedPreference === 'language' && (
          <div
            aria-label={t('languageOptions')}
            aria-busy={localeSaving}
            className="absolute bottom-0 right-0 z-10 grid w-[calc(100%_-_0.75rem)] rounded-xl border border-border bg-elevated p-1.5 shadow-2xl sm:right-[calc(100%+0.5rem)] sm:w-48"
            id="language-options"
            role="group"
          >
            {LOCALE_OPTIONS.map(option => (
              <button
                className={cn(
                  optionClass,
                  locale === option.value
                    ? 'bg-hover text-foreground'
                    : 'text-muted hover:bg-hover hover:text-foreground'
                )}
                key={option.value}
                disabled={localeSaving}
                onClick={() => void selectLocale(option.value)}
                type="button"
              >
                <span className="flex-1">{option.label}</span>
                {locale === option.value && (
                  <Icon className="size-4" name="check" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid p-1.5">
        {workspaceContracts.length > 1 ? (
          <button
            className={cn(
              optionClass,
              'text-muted hover:bg-hover hover:text-foreground'
            )}
            onClick={onSwitchWorkspace}
            ref={workspaceTriggerRef}
            type="button"
          >
            <Icon className="size-[18px]" name="switch" />
            {t('switchWorkspace')}
          </button>
        ) : (
          <Link
            className={cn(
              optionClass,
              'text-muted hover:bg-hover hover:text-foreground'
            )}
            onClick={close}
            to="/workspaces"
          >
            <Settings
              aria-hidden="true"
              className="size-[18px]"
              strokeWidth={1.7}
            />
            {t('workspaceSettings')}
          </Link>
        )}
        <button
          className={cn(
            optionClass,
            'text-muted hover:bg-hover hover:text-foreground'
          )}
          onClick={() => void signOut()}
          type="button"
        >
          <Icon className="size-[18px]" name="logout" />
          {t('logOut')}
        </button>
      </div>
    </div>
  );
};
